import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Sparkline } from './Sparkline';
import { PeriodBadge } from './PeriodBadge';
import type { RestaurantSemData } from '@/types/sem';
import { normalizeRestaurantName } from '@/lib/normalize-names';
import type { DailySnapshotRow } from '@/lib/supabase-to-sem';
import { comparePeriods } from '@/lib/supabase-to-sem';

interface PropertyBreakdownProps {
  restaurants: RestaurantSemData[];
  dailySnapshots: DailySnapshotRow[];
  prevSnapshots: DailySnapshotRow[];
}

function formatCurrency(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(Math.round(value));
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function getBorderColor(roas: number): string {
  if (roas >= 4) return 'border-l-[var(--ed-green)]';
  if (roas >= 2) return 'border-l-[var(--ed-amber)]';
  return 'border-l-[var(--ed-red)]';
}

function getRoasColor(roas: number): string {
  if (roas >= 4) return 'text-[var(--ed-green)]';
  if (roas >= 2) return 'text-[var(--ed-amber)]';
  return 'text-[var(--ed-red)]';
}

interface PropertyCardProps {
  restaurant: RestaurantSemData;
  snapshots: DailySnapshotRow[];
  prevSnaps: DailySnapshotRow[];
}

function PropertyCard({ restaurant, snapshots, prevSnaps }: PropertyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { summary } = restaurant.data;
  const campaignCount = restaurant.data.campaigns.length;
  const adGroupCount = restaurant.data.campaigns.reduce((s, c) => s + c.adGroups.length, 0);

  const roasComparison = comparePeriods(snapshots, prevSnaps, 'roas');

  // Sparkline data: conversions per day for this restaurant
  const dateMap = new Map<string, number>();
  for (const s of snapshots) {
    dateMap.set(s.date, (dateMap.get(s.date) || 0) + s.conversions);
  }
  const sortedDates = Array.from(dateMap.keys()).sort();
  const convsByDay = sortedDates.map(d => dateMap.get(d) || 0);

  return (
    <div className={`ed-card border-l-2 ${getBorderColor(summary.roas)} overflow-hidden`}>
      {/* Header — clickable */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-[var(--ed-bg-2)] transition-colors"
      >
        <div className="flex items-center gap-3">
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          <div>
            <p className="text-sm font-medium">{normalizeRestaurantName(restaurant.name)}</p>
            <p className="text-[0.65rem] text-muted-foreground">
              {campaignCount} campaign{campaignCount !== 1 ? 's' : ''} • {adGroupCount} ad group{adGroupCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <p className={`text-sm font-medium ${getRoasColor(summary.roas)}`}>{summary.roas.toFixed(2)}x</p>
            <p className="text-[0.6rem] text-muted-foreground/60">ROAS</p>
          </div>
          <div className="text-right">
            <p className="text-sm">{formatCurrency(summary.spend)}</p>
            <p className="text-[0.6rem] text-muted-foreground/60">Spend</p>
          </div>
          <div className="text-right">
            <p className="text-sm">{formatNumber(summary.conversions)}</p>
            <p className="text-[0.6rem] text-muted-foreground/60">Conv</p>
          </div>
          <PeriodBadge
            percentChange={roasComparison.percentChange}
            direction={roasComparison.direction}
          />
        </div>
      </button>

      {/* Expanded detail */}
      <div className={`overflow-hidden transition-all duration-200 ${
        expanded ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 pt-2 border-t border-border/30">
          <div className="grid grid-cols-[auto_1fr] gap-3">
            {/* Mini metric cards */}
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { label: 'CTR', value: formatPercent(summary.ctr) },
                { label: 'CPC', value: formatCurrency(summary.cpc, 2) },
                { label: 'CPA', value: formatCurrency(summary.cpa, 2) },
                { label: 'Avg QS', value: `${Math.round(summary.qualityScoreAvg)}/10` },
              ].map(m => (
                <div key={m.label} className="px-2.5 py-1.5 rounded-md bg-white/[0.02]">
                  <div className="text-[0.55rem] text-muted-foreground/40 font-medium uppercase tracking-wider">{m.label}</div>
                  <div className="text-[0.8rem] font-display tabular-nums">{m.value}</div>
                </div>
              ))}
            </div>
            {/* Sparkline chart */}
            <div className="rounded-md px-3 pt-2 pb-3 bg-white/[0.015]">
              <div className="text-[0.55rem] text-muted-foreground/40 uppercase tracking-wider mb-3">Daily Conversions</div>
              <Sparkline
                data={convsByDay}
                labels={sortedDates.map(d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}
                height={44}
                interactive
                color="rgba(255,255,255,0.25)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function PropertyBreakdown({ restaurants, dailySnapshots, prevSnapshots }: PropertyBreakdownProps) {
  // Filter out $0 spend restaurants, then sort by ROAS descending
  const sorted = [...restaurants]
    .filter(r => r.data.summary.spend > 0)
    .sort((a, b) => b.data.summary.roas - a.data.summary.roas);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="ed-section-label">
          Property Breakdown
        </div>
        <div className="text-[0.65rem] text-muted-foreground/50">Sorted by ROAS ▾</div>
      </div>
      <div className="space-y-2">
        {sorted.map((r) => (
          <PropertyCard
            key={r.id}
            restaurant={r}
            snapshots={dailySnapshots.filter(s => s.restaurant_id === r.id)}
            prevSnaps={prevSnapshots.filter(s => s.restaurant_id === r.id)}
          />
        ))}
      </div>
    </div>
  );
}
